import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  ImageField,
  ImageInput,
  List,
  NumberField,
  SimpleForm,
  TextField,
  TextInput,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useRedirect,
} from "react-admin";

import { Megaphone } from "lucide-react";
import { EditorToolbar } from "../../components";
import { authClient } from "../../server";

const LOCATION_SLUG_BY_SOURCE = {
  display_on_player: "tisha-bav-player-overlay",
  display_below_player: "tisha-bav-below-player",
  display_below_lecture_player: "tisha-bav-below-lecture-player",
  display_on_schedule: "tisha-bav-schedule",
};

const StatsContext = createContext({ stats: {}, isLoading: false });

function formatStat(value) {
  return Number(value || 0).toLocaleString();
}

function StatsHeader({ year, setYear }) {
  const currentYear = new Date().getFullYear();

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 8 }}>
        Stats format: exposures / impressions / clicks. Default period: current
        calendar year.
      </div>
      <button
        type="button"
        onClick={() => setYear(currentYear)}
        disabled={year === currentYear}
        style={{ marginRight: 8 }}
      >
        This Year
      </button>
      <button type="button" onClick={() => setYear(null)} disabled={!year}>
        All Time
      </button>
    </div>
  );
}

function LocationStatsField({ source }) {
  const record = useRecordContext();
  const { stats, isLoading, isError } = useContext(StatsContext);

  if (!record) return null;

  const locationStats = stats?.[record.id]?.[LOCATION_SLUG_BY_SOURCE[source]];

  return (
    <span>
      <BooleanField source={source} />
      <br />
      <span title="exposures / impressions / clicks">
        {isLoading
          ? "…"
          : isError
            ? "—"
            : `${formatStat(locationStats?.exposures)}/${formatStat(
                locationStats?.impressions,
              )}/${formatStat(locationStats?.clicks)}`}
      </span>
    </span>
  );
}

function StatsDatagrid({ year }) {
  const { data } = useListContext();
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const ids = useMemo(() => (data || []).map((record) => record.id), [data]);
  const idParam = ids.join(",");

  useEffect(() => {
    if (!idParam) {
      setStats({});
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let active = true;
    setIsLoading(true);
    setIsError(false);

    const yearParam = year ? `&year=${year}` : "";

    authClient
      .get(`/admin/tisha_bav_ad/stats?ids=${idParam}${yearParam}`)
      .then(({ json }) => {
        if (active) {
          setStats(json || {});
        }
      })
      .catch(() => {
        if (active) {
          setStats({});
          setIsError(true);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [idParam, year]);

  return (
    <StatsContext.Provider value={{ stats, isLoading, isError }}>
      <Datagrid rowClick="edit">
        <NumberField source="id" />
        <TextField source="link" />
        <ImageField source="img" sortable={false} />
        <LocationStatsField
          source="display_on_player"
          label="Show on player"
          sortable={false}
        />
        <LocationStatsField
          source="display_below_player"
          label="Show below player"
          sortable={false}
        />
        <LocationStatsField
          source="display_below_lecture_player"
          label="Show below lecture player"
          sortable={false}
        />
        <LocationStatsField
          source="display_on_schedule"
          label="Show on schedule list"
          sortable={false}
        />
      </Datagrid>
    </StatsContext.Provider>
  );
}

function ListView(props) {
  const [year, setYear] = useState(new Date().getFullYear());

  return (
    <List {...props} bulkActionButtons={false}>
      <StatsHeader year={year} setYear={setYear} />
      <StatsDatagrid year={year} />
    </List>
  );
}
function renderEditForm() {
  return (
    <SimpleForm toolbar={<EditorToolbar />}>
      <BooleanInput
        source="display_on_player"
        label="Show on player (ads will rotate every 5 minutes and show for 10 seconds)"
      />
      <BooleanInput
        source="display_below_player"
        label="Show below player (ads will rotate every 5 minutes and show for 10 seconds)"
      />
      <BooleanInput
        source="display_below_lecture_player"
        label="Show below the regular lecture player (ads rotate every 3 minutes)"
      />
      <BooleanInput
        source="display_on_schedule"
        label="Show on schedule list"
      />
      <TextInput source="link" />
      <br />
      <br />
      <h4>Image</h4>
      <p style={{ width: 600 }}>
        Image aspect ratio 10:1 (ideal size 1400x140)
      </p>
      <TextInput source="img" label="Image URL" />
      <p>-or-</p>
      <ImageInput
        source="FILE_photo"
        label="Image Upload"
        accept="image/*"
        placeholder={<p>Click or drop a photo here.</p>}
        touch="true"
      >
        <ImageField source="src" title="title" />
      </ImageInput>
    </SimpleForm>
  );
}

function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Tisha bav ad created successfully!", {
            type: "success",
          });
          refresh(); // Ensures UI updates with the new data
          redirect("list", "tisha_bav_ad");
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderEditForm()}
    </Create>
  );
}

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Tisha bav ad updated successfully!", {
            type: "success",
          });
          refresh(); // Ensures UI updates with the new data
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {renderEditForm()}
    </Edit>
  );
}

export const TbavAds = {
  category: "tbav",
  name: "tisha_bav_ad",
  create: CreateView,
  edit: EditView,
  icon: Megaphone,
  list: ListView,
  options: { label: "Tisha B'av Ads" },
};
